import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DataObjectContextValue } from './DataObjectContext.types';
import {
  defaultAlternativeFilters,
  defaultRequiredKeyFilters,
  defaultValidationFilters,
  useAlternativeFilter,
  useKeysFilter,
  useValidationFilter,
} from './validation/utils';

const DataObjectContext = createContext<DataObjectContextValue>({
  filterCount: 0,
  alternativeFilters: defaultAlternativeFilters,
  filters: defaultValidationFilters,
  requiredKeyFilters: defaultRequiredKeyFilters,
  validateKeysModalOpen: false,
  focusedKeyId: undefined,
  setAlternativeFilters: () => undefined,
  setFilters: () => undefined,
  setRequiredKeyFilters: () => undefined,
  filteredAlgorithmIds: undefined,
  setFilteredAlgorithmIds: () => undefined,
  setValidateKeysModalOpen: () => undefined,
  setFocusedKeyId: () => undefined,
});

export const DataObjectContextProvider = ({ children }: PropsWithChildren) => {
  const [filteredAlgorithmIds, setFilteredAlgorithmIds] = useState<
    number[] | undefined
  >(undefined);
  const { alternativeFilters, setAlternativeFilters } = useAlternativeFilter();
  const { requiredKeyFilters, setRequiredKeyFilters } = useKeysFilter();
  const { filterCount, filters, setFilters } = useValidationFilter();
  const [validateKeysModalOpen, setValidateKeysModalOpen] =
    useState<boolean>(false);
  const [focusedKeyId, setFocusedKeyId] = useState<string | undefined>(
    undefined,
  );

  const value = useMemo(() => {
    return {
      alternativeFilters,
      filterCount,
      filters,
      requiredKeyFilters,
      validateKeysModalOpen,
      focusedKeyId,
      setAlternativeFilters,
      setFilters,
      setRequiredKeyFilters,
      filteredAlgorithmIds,
      setFilteredAlgorithmIds,
      setValidateKeysModalOpen,
      setFocusedKeyId,
    };
  }, [
    alternativeFilters,
    filterCount,
    filters,
    requiredKeyFilters,
    validateKeysModalOpen,
    focusedKeyId,
    setAlternativeFilters,
    setFilters,
    setRequiredKeyFilters,
    filteredAlgorithmIds,
    setFilteredAlgorithmIds,
    setValidateKeysModalOpen,
    setFocusedKeyId,
  ]);

  return (
    <DataObjectContext.Provider value={value}>
      {children}
    </DataObjectContext.Provider>
  );
};

export function useDataObjectContext() {
  const context = useContext(DataObjectContext);

  if (context === undefined) {
    throw new Error(
      'useDataObjectContext must be used within a DataObjectContextProvider',
    );
  }

  return context;
}
